import { COMPANY_EMPLOYMENT_TYPE_OPTIONS } from '../../employmentType'
import {
  CompanyFactKey,
  CompanyFactSchemaVersion,
  ConflictStrategy,
  type FactKeyConfig,
} from '../core'

export enum IndustryOption {
  THREE_PL = 'THREE_PL',
  BEVERAGE_PRODUCTION = 'BEVERAGE_PRODUCTION',
  CONTRACT_MANUFACTURING = 'CONTRACT_MANUFACTURING',
  CONTRACT_PACKAGING = 'CONTRACT_PACKAGING',
  COSMETICS_PRODUCTION = 'COSMETICS_PRODUCTION',
  CROSS_DOCK_DISTRIBUTION = 'CROSS_DOCK_DISTRIBUTION',
  DISTRIBUTION = 'DISTRIBUTION',
  EVENTS = 'EVENTS',
  FINAL_MILE = 'FINAL_MILE',
  FOOD_PREPARATION_AND_SERVICE = 'FOOD_PREPARATION_AND_SERVICE',
  FOOD_PRODUCTION = 'FOOD_PRODUCTION',
  FULFILLMENT = 'FULFILLMENT',
  FURNITURE_PRODUCTION_AND_DISTRIBUTION = 'FURNITURE_PRODUCTION_AND_DISTRIBUTION',
  MANUFACTURING = 'MANUFACTURING',
  PRINTING = 'PRINTING',
  TIER_PRODUCTION_AND_DISTRIBUTION = 'TIER_PRODUCTION_AND_DISTRIBUTION',
  WAREHOUSE = 'WAREHOUSE',
}

export enum SuccessMetricOption {
  FILL_RATE = 'FILL_RATE',
  RETENTION = 'RETENTION',
  RELIABILITY = 'RELIABILITY',
  ON_TIME_RATE = 'ON_TIME_RATE',
  GMP_COMPLIANCE = 'GMP_COMPLIANCE',
  CONVERSIONS = 'CONVERSIONS',
}

export enum FacilityConditionOption {
  INDOOR = 'INDOOR',
  OUTDOOR = 'OUTDOOR',
  CLIMATE_CONTROLLED = 'CLIMATE_CONTROLLED',
  NOT_CLIMATE_CONTROLLED = 'NOT_CLIMATE_CONTROLLED',
  COLD_FACILITY = 'COLD_FACILITY',
  HOT_FACILITY = 'HOT_FACILITY',
  FREEZER = 'FREEZER',
  LOUD = 'LOUD',
  QUIET = 'QUIET',
  DISORGANIZED = 'DISORGANIZED',
  CLEAN = 'CLEAN',
  OTHER = 'OTHER',
}

export enum CulturalEnvironmentOption {
  ISOLATED = 'ISOLATED',
  COLLABORATIVE = 'COLLABORATIVE',
  FAST_PACED = 'FAST_PACED',
  RELAXED = 'RELAXED',
  CLEAR_INSTRUCTIONS = 'CLEAR_INSTRUCTIONS',
  CONFUSING_INSTRUCTIONS = 'CONFUSING_INSTRUCTIONS',
  FRIENDLY_SUPERVISORS = 'FRIENDLY_SUPERVISORS',
  INTENSE_SUPERVISORS = 'INTENSE_SUPERVISORS',
  UNSAFE_EQUIPMENT_OPERATION = 'UNSAFE_EQUIPMENT_OPERATION',
  OTHER = 'OTHER',
}

export enum DemographicsOption {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  WHITE = 'WHITE',
  BLACK = 'BLACK',
  HISPANIC = 'HISPANIC',
  ASIAN = 'ASIAN',
  ARAB = 'ARAB',
  ENGLISH = 'ENGLISH',
  SPANISH = 'SPANISH',
  CREOLE = 'CREOLE',
  YOUNG = 'YOUNG',
  MIDDLE_AGED = 'MIDDLE_AGED',
  OLD = 'OLD',
  OTHER = 'OTHER',
}

export const BUSINESS_PROFILE_KEY_CONFIG: Partial<
  Record<CompanyFactKey, FactKeyConfig>
> = {
  [CompanyFactKey.BUSINESS_PROFILE_WORKER_CLASSIFICATION]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Worker Classification',
    options: COMPANY_EMPLOYMENT_TYPE_OPTIONS,
  },
  [CompanyFactKey.BUSINESS_PROFILE_INDUSTRY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Industry',
    options: Object.values(IndustryOption),
  },
  [CompanyFactKey.BUSINESS_PROFILE_PAIN_POINTS]: {
    schemaVersion: CompanyFactSchemaVersion.PAIN_POINT_V1,
    displayName: 'Pain Points',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'Primary issues & struggles they have faced with their existing staffing solution.',
  },
  [CompanyFactKey.BUSINESS_PROFILE_SUCCESS_METRICS]: {
    schemaVersion: CompanyFactSchemaVersion.SUCCESS_METRIC_V1,
    displayName: 'Success Metrics',
    options: Object.values(SuccessMetricOption),
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'What the company measures us on (e.g. fill rate, retention, reliability). Distinct from Pain Points (issues with their prior agency).',
  },
  [CompanyFactKey.BUSINESS_PROFILE_STAFFING_MODEL]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Staffing Model',
    conflictStrategy: ConflictStrategy.APPEND,
    purposeTooltip:
      'How the company uses contingent labor: partnership style, demand patterns, peak seasons, volume expectations. Distinct from Pain Points (issues with prior agency) and Success Metrics (what they measure us on).',
  },
  [CompanyFactKey.BUSINESS_PROFILE_ONSITE_STRATEGY]: {
    schemaVersion: CompanyFactSchemaVersion.ONSITE_STRATEGY_V1,
    displayName: 'Onsite Strategy',
    conflictStrategy: ConflictStrategy.REPLACE,
    purposeTooltip:
      "Whether an onsite Traba lead is required, who pays for them, the lead's classification (full-time, part-time, or working shift lead), and which responsibilities they hold. Distinct from Staffing Model (which describes broader contingent-labor patterns).",
  },
  [CompanyFactKey.BUSINESS_PROFILE_FACILITY_CONDITIONS]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Facility Conditions',
    options: Object.values(FacilityConditionOption),
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'Physical attributes of the work environment (temperature, noise, organization). Each option a worker might care about when deciding to take a shift.',
  },
  [CompanyFactKey.BUSINESS_PROFILE_CULTURAL_ENVIRONMENT]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Cultural Environment',
    options: Object.values(CulturalEnvironmentOption),
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'Social and operational tone of the worksite — pace, supervisor style, instruction clarity, equipment safety.',
  },
  [CompanyFactKey.BUSINESS_PROFILE_DEMOGRAPHICS]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Demographics',
    options: Object.values(DemographicsOption),
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'Workforce composition observed on-site (genders, races, languages spoken, age groups).',
  },
  [CompanyFactKey.BUSINESS_PROFILE_CUSTOMER_PRIORITIES]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'What the Customer Cares About',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
    purposeTooltip:
      'What the customer cares most about in this partnership — captured free-form from discovery (e.g., "fill rate above all", "bilingual leads", "zero safety incidents"). Includes priorities Traba does not tangibly track. Distinct from Success Metrics (the measured ones).',
  },
  [CompanyFactKey.BUSINESS_PROFILE_NOTES]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Notes',
    conflictStrategy: ConflictStrategy.APPEND,
    purposeTooltip:
      "General free-form notes about the company's business profile that don't fit a more specific fact above.",
  },
}
